"use client";

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BillingIntervalToggle } from "@/components/subscribe/BillingIntervalToggle";
import { PlanPrice } from "@/components/subscribe/PlanPrice";
import { track } from "@/components/analytics/track";
import { Link } from "@/i18n/navigation";
import type { PublicPriceOffer } from "@/lib/pricing/offer";
import {
  buildSubscriptionPlans,
  type BillingInterval,
} from "@/lib/subscriptionPlans";

export function PricingPageView({ offer }: { offer: PublicPriceOffer }) {
  const t = useTranslations("Pricing");
  const tp = useTranslations("Plans");
  const locale = useLocale();
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>("monthly");
  const plans = useMemo(
    () => buildSubscriptionPlans(billingInterval, tp, locale, offer),
    [billingInterval, tp, locale, offer]
  );

  return (
    <>
      <Header />
      <div className="min-h-screen quant-grid pt-[calc(73px+var(--announcement-bar-height,0px))]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="text-center mb-10"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-slate-100 mb-4">
              {t("title")}
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto mb-4">
              {t.rich("intro", {
                refund: (chunks) => (
                  <Link
                    href="/refund-policy"
                    className="text-teal-light underline"
                  >
                    {chunks}
                  </Link>
                ),
              })}
            </p>
            <p className="text-slate-400 text-sm max-w-2xl mx-auto mb-4">
              {t("downloadFirst")}
            </p>
            <p className="text-slate-500 text-sm max-w-2xl mx-auto mb-8">
              {t("paymentNote")}
            </p>
            <div className="flex justify-center">
              <BillingIntervalToggle
                value={billingInterval}
                onChange={setBillingInterval}
              />
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
                className={`flex flex-col rounded-xl border p-8 bg-slate-800/30 ${
                  plan.highlighted
                    ? "border-teal-primary ring-2 ring-teal-primary/25"
                    : "border-slate-700"
                }`}
              >
                {plan.highlighted ? (
                  <span className="inline-block self-start px-3 py-0.5 text-xs font-semibold text-teal-light bg-teal-primary/20 rounded-full mb-4">
                    {tp("popular")}
                  </span>
                ) : plan.id === "starter" ? (
                  <span className="inline-block self-start px-3 py-0.5 text-xs font-semibold text-teal-light bg-teal-primary/20 rounded-full mb-4">
                    {tp("trialBadge")}
                  </span>
                ) : null}
                <h2 className="text-xl font-semibold text-slate-100 mb-1">
                  {plan.name}
                </h2>
                <PlanPrice
                  price={plan.price}
                  period={plan.period}
                  compareAtPrice={plan.compareAtPrice}
                  promotionText={plan.promotionText}
                />
                {plan.priceSublabel ? (
                  <p className="text-xs text-teal-light/80 mb-2">
                    {plan.priceSublabel}
                  </p>
                ) : null}
                <p className="text-slate-400 text-sm mb-6">{plan.description}</p>
                {plan.id === "starter" ? (
                  <p className="text-xs text-teal-light/90 mb-4">{tp("trialToday")}</p>
                ) : null}
                <ul className="space-y-2 mb-8 flex-1">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-center gap-2 text-slate-300 text-sm"
                    >
                      <Check className="w-4 h-4 text-electric-blue shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                {plan.id === "enterprise" ? (
                  <Link
                    href="/subscribe?plan=enterprise"
                    onClick={() =>
                      track("cta_click", {
                        meta: {
                          label: "pricing_subscribe",
                          plan: plan.id,
                          interval: billingInterval,
                        },
                      })
                    }
                    className="block text-center w-full py-3 px-4 rounded-lg bg-maroon hover:bg-maroon-hover text-white font-semibold transition-colors"
                  >
                    {tp("contactUs")}
                  </Link>
                ) : (
                  <div className="space-y-3">
                    <Link
                      href={`/product?plan=${plan.id}&interval=${billingInterval}`}
                      onClick={() =>
                        track("cta_click", {
                          meta: {
                            label: "pricing_download",
                            plan: plan.id,
                            interval: billingInterval,
                          },
                        })
                      }
                      className="block text-center w-full py-3 px-4 rounded-lg bg-maroon hover:bg-maroon-hover text-white font-semibold transition-colors"
                    >
                      {plan.id === "starter" ? tp("trialCta") : tp("downloadApp")}
                    </Link>
                    <Link
                      href={`/subscribe?plan=${plan.id}&interval=${billingInterval}`}
                      onClick={() =>
                        track("cta_click", {
                          meta: {
                            label: "pricing_subscribe",
                            plan: plan.id,
                            interval: billingInterval,
                          },
                        })
                      }
                      className="block text-center text-sm text-teal-light hover:text-teal-400 underline underline-offset-2"
                    >
                      {tp("subscribeIfReady")}
                    </Link>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
